/**
 * Numeric utility functions shared across the application.
 *
 * - `round1` / `round2` — financial rounding helpers used in every price-entry path.
 * - `ExcelDateToJSDate` — converts an Excel serial date (days since 1900-01-00) to a JS `Date`.
 * - `parseMyFloatEx` — locale-tolerant float parser that handles `$1,234.56`, `1 234,56`,
 *   trailing currency symbols, and auto-detects decimal vs. thousand separator.
 * - `parseMyFloat` — simpler variant: strips `$€£` and commas, then calls `parseFloat`.
 * - `parseTextBuffer` — splits a tab/CSV text block into an array of objects keyed by the
 *   first-row headers; used by the Excel-import dialog's text-paste mode.
 */

/** Rounds `n` to 2 decimal places (standard financial rounding). */
export function round2(n: number) {
  return Math.round((n * 100)) / 100;
}

/** Rounds `n` to 1 decimal place. */
export function round1(n: number) {
  return Math.round((n * 10)) / 10;
}

/**
 * Strips VAT out of a gross amount: `gross / ((100 + vatPercent) / 100)`.
 * `vatPercent` is the product's real `PRODVAT` (18, 20 or 21 ...), never a constant.
 * A missing `vatPercent` yields `NaN`, exactly like the `(100 + PRODVAT) / 100` re-apply step.
 */
export function stripVat(gross: number, vatPercent: number): number {
  return gross / ((100.0 + vatPercent) / 100.0);
}

/**
 * Converts an Excel OLE Automation date serial (e.g. `44358` = 2021-06-07)
 * to a JavaScript `Date`, preserving time-of-day from the fractional part.
 */
export function ExcelDateToJSDate(serial) {
  var utc_days = Math.floor(serial - 25569);
  var utc_value = utc_days * 86400;
  var date_info = new Date(utc_value * 1000);

  var fractional_day = serial - Math.floor(serial) + 0.0000001;

  var total_seconds = Math.floor(86400 * fractional_day);

  var seconds = total_seconds % 60;

  total_seconds -= seconds;

  var hours = Math.floor(total_seconds / (60 * 60));
  var minutes = Math.floor(total_seconds / 60) % 60;

  return new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate(), hours, minutes, seconds);
}

/**
 * Locale-tolerant float parser.
 * Strips leading currency symbols (`$€£`) and trailing non-numeric suffixes,
 * then auto-detects whether the rightmost separator is the decimal (`.` or `,`)
 * before calling `parseFloat`.  Handles arrays by joining elements first.
 * Returns `NaN` for null / undefined / non-string / non-number input.
 */
export function parseMyFloatEx(txt: string): number {
  if (!txt) {
    return NaN;
  }

  if (Array.isArray(txt)) {
    txt = txt.join('');
  }

  if (typeof txt === 'number') {
    return txt;
  }

  if (typeof txt !== 'string') {
    return NaN;
  }

  // Remove common currency symbols and trim
  let cleanedText = txt.replace(/[$€£]/g, '').trim();

  // Find the rightmost non-numeric character (excluding . , -)
  const match = cleanedText.match(/[^0-9.,-]+$/);
  if (match) {
    cleanedText = cleanedText.slice(0, match.index).trim();
  }

  // Decide decimal/thousand separator based on rightmost separator
  const lastDot = cleanedText.lastIndexOf('.');
  const lastComma = cleanedText.lastIndexOf(',');
  let normalizedText = cleanedText;

  if (lastDot > lastComma) {
    // Dot is decimal separator, remove all commas (thousand sep)
    normalizedText = cleanedText.replace(/,/g, '');
  } else if (lastComma > lastDot) {
    // Comma is decimal separator, remove all dots (thousand sep), replace last comma with dot
    normalizedText = cleanedText.replace(/\./g, '').replace(/,([^,]*)$/, '.$1');
  } else {
    // No separator or ambiguous, just remove spaces
    normalizedText = cleanedText.replace(/ /g, '');
  }

  return parseFloat(normalizedText);
}
/**
 * Simple float parser — strips `$€£`, whitespace, and commas, then calls `parseFloat`.
 * Assumes dot-decimal format.  Use `parseMyFloatEx` when the locale is unknown.
 * Returns `NaN` for null / undefined / non-string input.
 */
export function parseMyFloat(txt: string): number {
  if (!txt) {
    return NaN;
  }

  if (Array.isArray(txt)) {
    txt = txt.join('');
  }

  if (typeof txt === 'number') {
    return txt;
  }

  if (typeof txt !== 'string') {
    return NaN;
  }

  const cleanedText = txt.replace(/[$€£]/g, '').trim();
  const normalizedText = cleanedText.replace(/ /g, '').replace(/,/g, '').replace('.', '.');
  return parseFloat(normalizedText);
}

/**
 * Splits a tab/CSV text block into an array of row objects.
 * The first line is treated as headers; each subsequent line becomes an object
 * keyed by those headers.  Every row also gets a `Статус` field initialised to `''`
 * for use as an import-result status column.
 *
 * @param txt            The raw text to parse (e.g. from clipboard paste).
 * @param valueSeparator Column delimiter — `'\t'` for TSV, `';'` for CSV.
 */
export function parseTextBuffer(txt: string, valueSeparator: string): any {
  const [headerLine, ...lines] = txt.split('\n');

  // Split headers line into an array
  // `valueSeparator` may come from some kind of argument
  // You may want to transform header strings into something more
  // usable, like `camelCase` or `lowercase-space-to-dash`

  const headers = headerLine.split(valueSeparator).map(header => header.trim());

  const objects = lines
    .map((line, index) => {
      return { 'Статус': '', ...line.split(valueSeparator).reduce((object, value, index) => ({ ...object, [headers[index]]: value.trim(), }), {}) };
    });

  return objects;
}
